import React, { useState, useEffect, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  useCategorySeries,
  useSeriesDetails,
} from "../../../hooks/useProducts";
import useMobile from "../../../hooks/useMobile";
import Slider from "../../Slider/Slider";
import loaderIcon from "../../../images/icons/loader.svg";
import downloadIcon from "../../../images/icons/download_dark.svg";
import pdfIcon from "../../../images/icons/pdf_icon.svg";
import xlsxIcon from "../../../images/icons/xlsx_icon.svg";
import "../../../styles/productsDetails.css";

const ProductsDetails = () => {
  const { categoryId, seriesName: seriesNameParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMobile();
  // prettier-ignore
  const seriesName = seriesNameParam ? decodeURIComponent(seriesNameParam): null;
  const [activeTab, setActiveTab] = useState("description");

  // Получаем seriesId из state или вычисляем из списка серий
  const seriesIdFromState = location.state?.seriesId;

  // Получаем список серий категории (если seriesId нет в state)
  const { data: categorySeriesData } = useCategorySeries(
    !seriesIdFromState && categoryId ? categoryId : null
  );

  // Вычисляем seriesId: из state или из списка серий
  const seriesId = useMemo(() => {
    if (seriesIdFromState) {
      return seriesIdFromState;
    }
    if (categorySeriesData?.result && seriesName) {
      const foundSeries = categorySeriesData.result.find(
        (s) => s.name === seriesName
      );
      return foundSeries?.id || null;
    }
    return null;
  }, [seriesIdFromState, categorySeriesData, seriesName]);

  // Получаем детали серии
  const {
    data: seriesData,
    isLoading,
    isError,
    error,
  } = useSeriesDetails(seriesId);

  // Очищаем pageTitle при монтировании, чтобы не показывать categoryName в title
  useEffect(() => {
    if (
      location.state?.pageTitle &&
      location.state?.pageTitle === location.state?.categoryName
    ) {
      navigate(location.pathname, {
        replace: true,
        state: {
          ...location.state,
          pageTitle: null,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Обновление location.state после получения данных
  useEffect(() => {
    if (!seriesName) return;

    // Если seriesId не найден, просто ждем загрузки списка серий
    if (!seriesId) return;

    // Если данные серии загружены, обновляем location.state
    if (seriesData?.name) {
      navigate(location.pathname, {
        replace: true,
        state: {
          ...location.state,
          pageTitle: seriesData.name,
          categoryName:
            seriesData.category_name || location.state?.categoryName,
          seriesId: seriesId,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesId, seriesData, seriesName, location.pathname, navigate]);

  // Сортируем изображения для слайдера (до early returns, чтобы хуки вызывались в одном порядке)
  const sortedImages = useMemo(() => {
    if (!seriesData?.images) return [];
    return [...seriesData.images].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
  }, [seriesData?.images]);

  const openDocument = (url) => {
    if (!url) return;

    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getDocumentIcon = (url) => {
    if (!url) return pdfIcon;
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes(".xlsx") || lowerUrl.includes(".xls")) {
      return xlsxIcon;
    }
    return pdfIcon;
  };

  if (isLoading) {
    return (
      <div className="loader">
        <img src={loaderIcon} alt="loader" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="page-width">
        Ошибка загрузки информации о серии:{" "}
        {error?.message || "Неизвестная ошибка"}
      </div>
    );
  }

  if (!seriesData) {
    return <div className="page-width">Данные не найдены</div>;
  }

  // Получаем параметры для вкладки "Характеристики" (сортируем по полю sort)
  const allParameters = [...(seriesData.parameters || [])].sort(
    (a, b) => (a.sort ?? 0) - (b.sort ?? 0)
  );

  // Функция для переключения на вкладку характеристик и скролла
  const scrollToCharacteristics = () => {
    setActiveTab("characteristics");
    setTimeout(() => {
      const tabsWrapper = document.querySelector(
        ".products-details-tabs-wrapper"
      );
      if (tabsWrapper) {
        tabsWrapper.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  // Функция для переключения на вкладку описания и скролла
  const scrollToDescription = () => {
    setActiveTab("description");
    setTimeout(() => {
      const tabsWrapper = document.querySelector(
        ".products-details-tabs-wrapper"
      );
      if (tabsWrapper) {
        tabsWrapper.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  return (
    <div className="products-details page-width">
      {/* Верхний блок: Слайдер и Описание/Характеристики в одной строке */}
      <div className="products-details-content">
        {/* Левая колонка: Слайдер изображений */}
        <div className="products-details-left">
          {sortedImages.length > 0 && (
            <Slider
              images={sortedImages}
              preview={!isMobile}
              autoPlay={isMobile}
              alt={seriesData.name}
            />
          )}
        </div>

        {/* Правая колонка: Описание и характеристики (только для десктопа) */}
        {!isMobile && (
          <div className="products-details-right">
            {/* Блок с описанием */}
            {seriesData.short_description && (
              <div className="product-description-block">
                <h3>Описание</h3>
                <p>{seriesData.short_description}</p>
                <span
                  onClick={() => scrollToDescription()}
                  className="view-all-characteristics-link"
                >
                  Подробнее
                </span>
              </div>
            )}

            {/* Блок с основными характеристиками из short_parameters */}
            {seriesData.short_parameters &&
              seriesData.short_parameters.length > 0 && (
                <div className="product-main-characteristics">
                  <h3>Характеристики</h3>
                  <div className="params flex-column column-start w-100 gap-3x">
                    <div className="params__group flex-column column-start w-100 gap-3x">
                      {[...seriesData.short_parameters]
                        .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
                        .map((item) => (
                          <div key={item.id} className="params__item w-100">
                            <span className="text-s">{item.name}</span>
                            <span className="params__dots" />
                            <span className="text-s">{item.value}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                  <span
                    onClick={() => scrollToCharacteristics()}
                    className="view-all-characteristics-link"
                  >
                    Просмотреть все характеристики
                  </span>
                </div>
              )}
          </div>
        )}
      </div>

      {/* Нижний блок: Вкладки */}
      <div className="products-details-tabs-wrapper flex-column column-start">
        <div className="selection-tabs flex-column">
          <div className="tab-items_container flex-row">
            <div className="tab-items flex-row flex-between">
              <div
                className={`tab-item${
                  activeTab === "description" ? " active" : ""
                }`}
                onClick={() => setActiveTab("description")}
              >
                Описание
              </div>
              <div
                className={`tab-item${
                  activeTab === "characteristics" ? " active" : ""
                }`}
                onClick={() => setActiveTab("characteristics")}
              >
                Характеристики
              </div>
              {/* Временно скрыты */}
              {/* <div
                className={`tab-item${
                  activeTab === "documents" ? " active" : ""
                }`}
                onClick={() => setActiveTab("documents")}
              >
                Документы
              </div> */}
            </div>
          </div>
        </div>

        {/* Контент вкладок */}
        <div className="products-details-tabs-content w-100">
          {/* Вкладка "Описание" */}
          {activeTab === "description" && (
            <div className="product-description-tab w-100">
              {seriesData.full_description && (
                <p>{seriesData.full_description}</p>
              )}
            </div>
          )}

          {/* Вкладка "Документы" */}
          {activeTab === "documents" && (
            <div className="product-documents w-100">
              {seriesData.documents && seriesData.documents.length > 0 && (
                <div className="documents-list">
                  {seriesData.documents.map((doc) => (
                    <div key={doc.id} className="document-item">
                      <div className="document-info">
                        <img
                          src={getDocumentIcon(doc.url)}
                          alt="Document"
                          className="document-icon"
                        />
                        <span className="document-name">{doc.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => openDocument(doc.url)}
                        className="download-btn btn-outlined size-m flex-row"
                      >
                        <img alt="download" src={downloadIcon} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Вкладка "Характеристики" */}
          {activeTab === "characteristics" && (
            <div className="product-parameters w-100">
              {allParameters.length > 0 && (
                <div className="params flex-column column-start w-100 gap-3x">
                  <div className="params__group flex-column column-start w-100 gap-3x">
                    {allParameters.map((item) => (
                      <div key={item.id} className="params__item w-100">
                        <span className="text-s">{item.name}</span>
                        <span className="params__dots" />
                        <span className="text-s">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductsDetails;
